import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
  import { Row, Col, Button, CardBody, Card } from "reactstrap";
import { Container, Spinner } from "reactstrap";
import MachineDowntimeTimeLine from "../Dashboard/MachineDownTimeTimeLine";
import MachineStatusLineChart from "../Dashboard/MachineStatusChart";
import DowntimeAnalyticsBar from "../Dashboard/DowntimeAnalyticsBar";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Breakdown from "./Breakdown";
import ProductionProReport from "./ProductionProReport";

/* ─── Helpers ──────────────────────────────────────────── */
const timeToHours = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return h + m / 60;
};

/* ─── Constants ──────────────────────────────────────────── */
const SHIFT_COLORS = {
  1: { bar: "#f05a57", label: "Shift 1  06:00–14:00" },
  2: { bar: "#d71515", label: "Shift 2  14:00–22:00" },
  3: { bar: "#ac1908", label: "Shift 3  22:00–06:00" },
};

/* ─── Styles (Standard Inputs) ──────────────────────────── */
const inputStyle = {
  height: 40,
  padding: "0 12px",
  fontSize: 14,
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#111",
  width: "100%",
};

const labelStyle = {
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 6,
  display: "block",
};

/* ─── Tooltip ────────────────────────────────────────────── */
const ShiftTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ddd",
        borderRadius: 8,
        padding: "10px 14px",
        fontSize: 13,
      }}
    >
      <p style={{ fontWeight: 500 }}>
        Day {label?.split("-")[2]}
      </p>
      {payload.map((p) => {
        const s = Number(p.dataKey.replace("s", ""));
        return (
          <p key={p.name} style={{ color: SHIFT_COLORS[s].bar }}>
            {SHIFT_COLORS[s].label}:{" "}
            <strong>{p.value?.toFixed(2)} h</strong>
          </p>
        );
      })}
    </div>
  );
};

/* ─── Main ──────────────────────────────────────────────── */
const MonthlyShiftStatus = () => {
  const [machines, setMachines] = useState([]);
  const [machineId, setMachineId] = useState("");
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}`;
  });

  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selected, setSelected] = useState(null);

  const detailRef = useRef(null);
  const selectedCompanyId = localStorage.getItem("companyid");

  /* ── Fetch Machines ── */
  useEffect(() => {
    fetch(
      `https://api.hanoch.in/api/machines/by-company/?id=${selectedCompanyId}`
    )
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : data?.data ?? [];
        setMachines(list);
        if (list.length) setMachineId(String(list[0].id));
      });
  }, []);

  /* ── Scroll ── */
  useEffect(() => {
    if (selected && detailRef.current) {
      setTimeout(() => {
        detailRef.current.scrollIntoView({ behavior: "smooth" });
      }, 80);
    }
  }, [selected]);

  /* ── Fetch Data ── */
  const fetchData = useCallback(() => {
    if (!machineId || !month) return;

    const [year, monthNum] = month.split("-");
    setLoading(true);
    setSubmitted(true);
    setChartData([]);
    setSelected(null);

    fetch(
      `https://api.hanoch.in/api/monthly-machine-status/?machine_id=${machineId}&year=${year}&month=${Number(
        monthNum
      )}`
    )
      .then((r) => r.json())
      .then((json) => {
        setSummary(json);

        const byDate = {};

        (json.data || []).forEach((row) => {
          const d = row.date;

          if (!byDate[d]) {
            byDate[d] = {
              date: d,
              s1: 0,
              s2: 0,
              s3: 0,
            };
          }

          byDate[d][`s${row.shift}`] = timeToHours(
            row.downtime_hours
          );
        });

        setChartData(
          Object.values(byDate).sort((a, b) =>
            a.date.localeCompare(b.date)
          )
        );
      })
      .finally(() => setLoading(false));
  }, [machineId, month]);
useEffect(() => {
  if (machineId && month) fetchData();
}, [machineId]);
  const monthlyRun = timeToHours(
    summary?.monthly_total_run_hours
  );
  const monthlyDown = timeToHours(
    summary?.monthly_total_downtime_hours
  );

  const machineName = summary?.machine_name || "";

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="OptiMize"
          breadcrumbItem="Down Time Analysis"
        />

    

<Row className="align-items-end mb-3">
  {/* Machine */}
  <Col md={5}>
    <label style={labelStyle}>Machine</label>
    <select
      value={machineId}
      onChange={(e) => setMachineId(e.target.value)}
      style={inputStyle}
    >
      {machines.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name || m.machine_name}
        </option>
      ))}
    </select>
  </Col>

  {/* Month */}
  <Col md={4}>
    <label style={labelStyle}>Month</label>
    <input
      type="month"
      value={month}
      onChange={(e) => setMonth(e.target.value)}
      style={inputStyle}
    />
  </Col>

  {/* Button */}
  <Col md={3}>
    <Button
      onClick={fetchData}
      disabled={loading}
      style={{ width: "100%", height: 40 }}
      color="primary"
    >
      {loading ? <Spinner size="sm" /> : "Show Report"}
    </Button>
  </Col>
</Row>
   {summary && (
 <Row>
        <Col xl={6} sm={6}>
          <Card>
            <CardBody>
              <div className="d-flex text-muted">
                <div className="flex-shrink-0 me-3 align-self-center">
                  {/* Replace RadialChart with icon */}
                  <div className="avatar-sm">
                    <div className="avatar-title bg-light rounded-circle text-primary" style={{ fontSize: '24px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="ri-time-line"></i>
                    </div>
                  </div>
                </div>

                <div className="flex-grow-1 overflow-hidden">
                  <p className="mb-1"> Total Running hours</p>
                   <h4>{monthlyRun.toFixed(1)} h</h4>
                  {/* <h5 className="mb-3">{partsData.total_running_hours}</h5> */}
                  {/* Commented section remains the same */}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={6} sm={6}>
          <Card>
            <CardBody>
              <div className="d-flex">
                <div className="flex-shrink-0 me-3 align-self-center">
                  {/* Replace RadialChart with icon */}
                  <div className="avatar-sm">
                    <div className="avatar-title bg-light rounded-circle text-success" style={{ fontSize: '24px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                  </div>
                </div>

                <div className="flex-grow-1 overflow-hidden">
                  <p className="mb-1"> TotalDown time hours</p>
                  <h4>{monthlyDown.toFixed(1)} h</h4>
                  {/* <h5 className="mb-3">{partsData.good_parts*part_multiple||0}</h5> */}
                  {/* Commented section remains the same */}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

     
      </Row>
        )}
      

        {/* TOP ALARMS */}
        {/* {summary?.monthly_top_alarms && (
          <div style={{ marginTop: 20 }}>
            <h5>Top Alarms</h5>
            {summary.monthly_top_alarms
              .slice(0, 5)
              .map((a, i) => (
                <div key={i}>
                  {a.alarm} — <b>{a.count}</b>
                </div>
              ))}
          </div>
        )} */}

        {/* CHART */}
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
          <XAxis
  dataKey="date"
  tickFormatter={(date) => date?.split("-")[2]}
/>
            <YAxis />
            <Tooltip content={<ShiftTooltip />} />
            <Legend />

            {[1, 2, 3].map((s) => (
              <Bar
                key={s}
                dataKey={`s${s}`}
                fill={SHIFT_COLORS[s].bar}
                onClick={(data) =>
                  setSelected({
                    date: data.date,
                    shift: s,
                  })
                }
              />
            ))}
          </BarChart>
        </ResponsiveContainer>

        {/* DETAILS */}
        {selected && (
          <div ref={detailRef} style={{ marginTop: 20 }}>
            <h5>
              {machineName} | {selected.date} | Shift{" "}
              {selected.shift}
            </h5>

            <MachineStatusLineChart
              selectedMachine={machineId}
              selectedDate={selected.date}
              selectedShift={selected.shift}
            />

            <MachineDowntimeTimeLine
              machineId={machineId}
              date={selected.date}
              shift={selected.shift}
            />
<Breakdown
 machineId={machineId}
              date={selected.date}
              shift={selected.shift}/>
            <DowntimeAnalyticsBar machineId={machineId} />
           
          </div>
        )}
    <ProductionProReport/>
        
      </Container>
    </div>
  );
};

export default MonthlyShiftStatus;